import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Chip, Portal, type Theme } from '@mui/material'

import { apiZodios } from '~api/apiZodios'
import { Button } from '~components/common'
import { Icon } from '~components/internal'
import { COMMIT_HASH, IS_DEMO, IS_PENTEST, IS_PROD, IS_STAGING } from '~config'

import { environmentIndicatorEvents } from '../events/environmentIndicatorEvents'

interface EnvironmentIndicatorProps {
  position?: 'top-right' | 'bottom-right' | 'bottom-left' | 'top-left'
  renderIndicator?: boolean
}

const positionStyles = {
  'top-left': {
    top: (theme: Theme): string => theme.spacing(2),
    left: (theme: Theme): string => theme.spacing(2),
  },
  'top-right': {
    top: (theme: Theme): string => theme.spacing(2),
    right: (theme: Theme): string => theme.spacing(2),
  },
  'bottom-left': {
    bottom: (theme: Theme): string => theme.spacing(2),
    left: (theme: Theme): string => theme.spacing(2),
  },
  'bottom-right': {
    bottom: (theme: Theme): string => theme.spacing(2),
    right: (theme: Theme): string => theme.spacing(2),
  },
}

export const CentralEnvironmentIndicator: React.FC = () => {
  const [position, setPosition] = useState<
    'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
  >('bottom-right')
  const [isLocalDb, setIsLocalDb] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const [isSnapping, setIsSnapping] = useState(false)

  const chipRef = useRef<HTMLDivElement>(null)
  const dragOriginRef = useRef({ x: 0, y: 0 })
  const offsetRef = useRef({ x: 0, y: 0 })
  const frameRef = useRef<number | null>(null)
  const preSnapRectRef = useRef<{ left: number; top: number } | null>(null)
  const [snapCount, setSnapCount] = useState(0)

  useEffect(() => {
    const unsubscribe = environmentIndicatorEvents.subscribe(setPosition)
    return unsubscribe
  }, [])

  if (IS_PROD) return null

  const getLocalDbCheck = async (): Promise<void> => {
    if (IS_STAGING || IS_DEMO || IS_PROD || IS_PENTEST) return
    const response = await apiZodios.api_app_localDBCheck({})
    setIsLocalDb(response.using_local_db)
  }

  useEffect(() => {
    getLocalDbCheck()
  }, [])

  const handleCommitClick = (e: React.MouseEvent): void => {
    e.stopPropagation()
    window.open(`https://github.com/polimorphic/poli-mono/commit/${COMMIT_HASH}`, '_blank')
  }

  const handleMouseDown = (e: React.MouseEvent): void => {
    // Don't start drag if clicking on the commit button
    if ((e.target as HTMLElement).closest('button')) return

    dragOriginRef.current = {
      x: e.clientX - offsetRef.current.x,
      y: e.clientY - offsetRef.current.y,
    }
    setIsSnapping(false)
    setIsDragging(true)
    e.preventDefault()
  }

  const handleMouseUp = useCallback((): void => {
    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current)
      frameRef.current = null
    }

    const rect = chipRef.current?.getBoundingClientRect()
    setIsDragging(false)

    if (!rect) return

    // Snap to the corner nearest the chip's center
    const isLeft = rect.left + rect.width / 2 < window.innerWidth / 2
    const isTop = rect.top + rect.height / 2 < window.innerHeight / 2
    const newPosition = `${isTop ? 'top' : 'bottom'}-${isLeft ? 'left' : 'right'}` as
      | 'top-left'
      | 'top-right'
      | 'bottom-left'
      | 'bottom-right'

    preSnapRectRef.current = { left: rect.left, top: rect.top }
    setSnapCount((count) => count + 1)
    setPosition(newPosition)
    environmentIndicatorEvents.setPosition(newPosition)
  }, [])

  const handleMouseMove = useCallback(
    (e: MouseEvent): void => {
      // The mouse button was released outside the window, so no mouseup reached us
      if (e.buttons === 0) {
        handleMouseUp()
        return
      }

      e.preventDefault()

      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current)

      const { clientX, clientY } = e
      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = null
        const next = {
          x: clientX - dragOriginRef.current.x,
          y: clientY - dragOriginRef.current.y,
        }
        offsetRef.current = next
        setOffset(next)
      })
    },
    [handleMouseUp],
  )

  // After snapping to a new corner, keep the chip visually in place, then animate it home
  useLayoutEffect(() => {
    const preSnapRect = preSnapRectRef.current
    if (!preSnapRect || !chipRef.current) return
    preSnapRectRef.current = null

    const rect = chipRef.current.getBoundingClientRect()
    const restingLeft = rect.left - offsetRef.current.x
    const restingTop = rect.top - offsetRef.current.y
    const held = { x: preSnapRect.left - restingLeft, y: preSnapRect.top - restingTop }

    offsetRef.current = held
    setOffset(held)

    // Two frames so the held offset is painted before the transition to zero starts
    let innerFrame: number | null = null
    const frame = requestAnimationFrame(() => {
      innerFrame = requestAnimationFrame(() => {
        offsetRef.current = { x: 0, y: 0 }
        setIsSnapping(true)
        setOffset({ x: 0, y: 0 })
      })
    })

    return (): void => {
      cancelAnimationFrame(frame)
      if (innerFrame !== null) cancelAnimationFrame(innerFrame)
    }
  }, [position, snapCount])

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)

      return (): void => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }
    }
  }, [isDragging, handleMouseMove, handleMouseUp])

  useEffect(
    () => (): void => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current)
    },
    [],
  )

  return (
    <Portal>
      <Chip
        tabIndex={-1}
        aria-hidden
        label={
          IS_DEMO || IS_PENTEST ? (
            IS_PENTEST ? (
              'PENTEST ENVIRONMENT'
            ) : (
              'DEMO ENVIRONMENT'
            )
          ) : IS_STAGING ? (
            <React.Fragment>
              STAGING ENVIRONMENT:{' '}
              <Button
                onClick={handleCommitClick}
                variant='text'
                size='small'
                tabIndex={-1}
                title={`View commit ${COMMIT_HASH} on GitHub`}
                sx={{ color: 'white' }}
              >
                {COMMIT_HASH}
              </Button>
            </React.Fragment>
          ) : (
            'LOCAL ENVIRONMENT'
          )
        }
        icon={
          !IS_DEMO && !IS_STAGING ? (
            isLocalDb ? (
              <Icon name='installDesktop' fontSize='medium' />
            ) : (
              <Icon name='cloudUpload' fontSize='medium' />
            )
          ) : undefined
        }
        size='medium'
        ref={chipRef}
        onMouseDown={handleMouseDown}
        style={{
          transform: `translate3d(${offset.x}px, ${offset.y}px, 0)`,
          transition: isSnapping
            ? 'transform 0.25s cubic-bezier(0.2, 0.8, 0.2, 1), opacity 0.2s ease-in-out'
            : 'opacity 0.2s ease-in-out',
        }}
        sx={{
          position: 'fixed',
          ...positionStyles[position],
          backgroundColor: (theme: Theme) =>
            IS_DEMO ? 'pink' : IS_STAGING ? theme.palette.success.main : theme.palette.primary.main,
          color: (theme: Theme) => theme.palette.warning.contrastText,
          cursor: isDragging ? 'grabbing' : 'grab',
          userSelect: 'none',
          opacity: isDragging ? 0.5 : 0.75,
          '&:hover': { opacity: isDragging ? 0.5 : 0.95 },
          backdropFilter: 'blur(2px)',
          willChange: 'transform',
          touchAction: 'none',
          zIndex: isDragging ? 9999 : 1000,
        }}
      />
    </Portal>
  )
}

// Use EnvironmentIndicator to set the position of the CentralEnvironmentIndicator component
const EnvironmentIndicator: React.FC<EnvironmentIndicatorProps> = ({
  position = 'bottom-right',
  renderIndicator = false,
}) => {
  useEffect(() => {
    environmentIndicatorEvents.setPosition(position)
  }, [position])

  if (renderIndicator) {
    return <CentralEnvironmentIndicator />
  }

  return null
}

export default EnvironmentIndicator
